'use server'

import { apiRequest, ApiResponse } from '../request'

export type PharmacyIntegrationType = 'MEMED' | 'NEXODATA' | 'CUSTOM'

export type ElectronicPrescriptionStatus =
    | 'PRESCRIPTION_CREATED'
    | 'PRESCRIPTION_SIGNED'
    | 'PRESCRIPTION_SENT'
    | 'PRESCRIPTION_VIEWED'
    | 'PRESCRIPTION_PARTIALLY_DISPENSED'
    | 'PRESCRIPTION_DISPENSED'
    | 'PRESCRIPTION_CANCELLED'
    | 'PRESCRIPTION_EXPIRED'

export interface PharmacyIntegration {
    id: number
    companyId: number
    name: string
    type: PharmacyIntegrationType
    apiUrl?: string
    apiKey?: string
    apiSecret?: string
    webhookUrl?: string
    webhookSecret?: string
    active: boolean
    settings?: any
    createdAt: string
    updatedAt: string
    _count?: {
        prescriptions: number
    }
}

export interface ElectronicPrescription {
    id: number
    companyId: number
    integrationId: number
    integration?: {
        id: number
        name: string
        type: PharmacyIntegrationType
    }
    prescriptionId: number
    prescription?: {
        id: number
        content: string
        prescriptionDate: string
        items?: any[]
    }
    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
        email?: string
    }
    professionalId: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
        councilName?: string
    }
    externalId?: string
    accessCode?: string
    prescriptionUrl?: string
    qrCode?: string
    status: ElectronicPrescriptionStatus
    validUntil?: string
    signedAt?: string
    sentAt?: string
    dispensedAt?: string
    signatureData?: any
    dispensingData?: any
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreatePharmacyIntegrationData {
    name: string
    type: PharmacyIntegrationType
    apiUrl?: string
    apiKey?: string
    apiSecret?: string
    active?: boolean
    settings?: any
}

export interface UpdatePharmacyIntegrationData
    extends Partial<CreatePharmacyIntegrationData> {}

export interface CreateElectronicPrescriptionData {
    integrationId: number
    prescriptionId: number
    clientId: number
    professionalId: number
    validUntil?: string
    metadata?: any
}

export interface SignPrescriptionData {
    signatureData: any
}

export interface PharmacyStats {
    totalIntegrations: number
    totalPrescriptions: number
    byStatus: Record<ElectronicPrescriptionStatus, number>
}

// Integration CRUD
export async function getPharmacyIntegrations(): Promise<
    ApiResponse<PharmacyIntegration[]>
> {
    return apiRequest<PharmacyIntegration[]>('pharmacy/integrations', {})
}

export async function getPharmacyIntegrationsByCompany(
    companyId: number
): Promise<ApiResponse<PharmacyIntegration[]>> {
    return apiRequest<PharmacyIntegration[]>(
        `pharmacy/integrations/company/${companyId}`,
        {}
    )
}

export async function getPharmacyIntegration(
    id: number
): Promise<ApiResponse<PharmacyIntegration>> {
    return apiRequest<PharmacyIntegration>(`pharmacy/integrations/${id}`, {})
}

export async function createPharmacyIntegration(
    data: CreatePharmacyIntegrationData
): Promise<ApiResponse<PharmacyIntegration>> {
    return apiRequest<PharmacyIntegration>(
        'pharmacy/integrations',
        { data },
        'post'
    )
}

export async function updatePharmacyIntegration(
    id: number,
    data: UpdatePharmacyIntegrationData
): Promise<ApiResponse<PharmacyIntegration>> {
    return apiRequest<PharmacyIntegration>(
        `pharmacy/integrations/${id}`,
        { data },
        'put'
    )
}

export async function deletePharmacyIntegration(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`pharmacy/integrations/${id}`, {}, 'delete')
}

export async function testPharmacyIntegration(
    id: number
): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest<{ success: boolean; message: string }>(
        `pharmacy/integrations/${id}/test`,
        {},
        'post'
    )
}

// Prescriptions
export async function getElectronicPrescriptions(filters?: {
    integrationId?: number
    clientId?: number
    professionalId?: number
    status?: ElectronicPrescriptionStatus
    startDate?: string
    endDate?: string
}): Promise<ApiResponse<ElectronicPrescription[]>> {
    return apiRequest<ElectronicPrescription[]>('pharmacy/prescriptions', {
        params: filters,
    })
}

export async function getElectronicPrescription(
    id: number
): Promise<ApiResponse<ElectronicPrescription>> {
    return apiRequest<ElectronicPrescription>(`pharmacy/prescriptions/${id}`, {})
}

export async function createElectronicPrescription(
    data: CreateElectronicPrescriptionData
): Promise<ApiResponse<ElectronicPrescription>> {
    return apiRequest<ElectronicPrescription>(
        'pharmacy/prescriptions',
        { data },
        'post'
    )
}

export async function signElectronicPrescription(
    id: number,
    data: SignPrescriptionData
): Promise<ApiResponse<ElectronicPrescription>> {
    return apiRequest<ElectronicPrescription>(
        `pharmacy/prescriptions/${id}/sign`,
        { data },
        'post'
    )
}

export async function sendElectronicPrescription(
    id: number
): Promise<ApiResponse<ElectronicPrescription>> {
    return apiRequest<ElectronicPrescription>(
        `pharmacy/prescriptions/${id}/send`,
        {},
        'post'
    )
}

export async function cancelElectronicPrescription(
    id: number,
    reason?: string
): Promise<ApiResponse<ElectronicPrescription>> {
    return apiRequest<ElectronicPrescription>(
        `pharmacy/prescriptions/${id}/cancel`,
        { data: { reason } },
        'post'
    )
}

export async function getElectronicPrescriptionQrCode(
    id: number
): Promise<ApiResponse<{ qrCode: string }>> {
    return apiRequest<{ qrCode: string }>(
        `pharmacy/prescriptions/${id}/qrcode`,
        {}
    )
}

export async function getClientPrescriptions(
    clientId: number
): Promise<ApiResponse<ElectronicPrescription[]>> {
    return apiRequest<ElectronicPrescription[]>(
        `pharmacy/client/${clientId}/prescriptions`,
        {}
    )
}

export async function getPharmacyStats(): Promise<ApiResponse<PharmacyStats>> {
    return apiRequest<PharmacyStats>('pharmacy/stats', {})
}

export const PHARMACY_TYPE_LABELS: Record<PharmacyIntegrationType, string> = {
    MEMED: 'Memed',
    NEXODATA: 'Nexodata',
    CUSTOM: 'Custom',
}

export const PRESCRIPTION_STATUS_LABELS: Record<
    ElectronicPrescriptionStatus,
    string
> = {
    PRESCRIPTION_CREATED: 'Criada',
    PRESCRIPTION_SIGNED: 'Assinada',
    PRESCRIPTION_SENT: 'Enviada',
    PRESCRIPTION_VIEWED: 'Visualizada',
    PRESCRIPTION_PARTIALLY_DISPENSED: 'Parcialmente Dispensada',
    PRESCRIPTION_DISPENSED: 'Dispensada',
    PRESCRIPTION_CANCELLED: 'Cancelada',
    PRESCRIPTION_EXPIRED: 'Expirada',
}

export const PRESCRIPTION_STATUS_COLORS: Record<
    ElectronicPrescriptionStatus,
    string
> = {
    PRESCRIPTION_CREATED: 'badge-info',
    PRESCRIPTION_SIGNED: 'badge-secondary',
    PRESCRIPTION_SENT: 'badge-warning',
    PRESCRIPTION_VIEWED: 'badge-info',
    PRESCRIPTION_PARTIALLY_DISPENSED: 'badge-warning',
    PRESCRIPTION_DISPENSED: 'badge-success',
    PRESCRIPTION_CANCELLED: 'badge-error',
    PRESCRIPTION_EXPIRED: 'badge-ghost',
}
